import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Camera, Image, Trash2, RefreshCw } from 'lucide-react'
import { employeeApi, getEmployeeData } from '../../lib/employeeApi'
import { Capacitor } from '@capacitor/core'

interface Photo {
  id: string; date: string; heure: string; mois: string; employeeName: string; fileName: string
}

// Traitement image : auto-rotation EXIF (canvas), crop centré portrait 3:4, resize 1200px max, JPEG 75%
function processImage(src: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => {
      const TARGET_W_H = 3 / 4 // portrait
      const MAX_H = 1200
      let srcW = img.naturalWidth, srcH = img.naturalHeight
      let srcX = 0, srcY = 0

      const ratio = srcW / srcH
      if (ratio > TARGET_W_H) {
        // Trop large : couper les côtés (centré)
        const newW = Math.round(srcH * TARGET_W_H)
        srcX = Math.round((srcW - newW) / 2)
        srcW = newW
      } else if (ratio < TARGET_W_H) {
        // Trop haut : garder le haut (l'étiquette est en haut)
        srcH = Math.round(srcW / TARGET_W_H)
      }

      const dstH = Math.min(srcH, MAX_H)
      const dstW = Math.round(dstH * TARGET_W_H)
      const canvas = document.createElement('canvas')
      canvas.width = dstW
      canvas.height = dstH
      const ctx = canvas.getContext('2d')!
      // drawImage corrige automatiquement l'orientation EXIF sur les navigateurs modernes
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, dstW, dstH)
      resolve(canvas.toDataURL('image/jpeg', 0.75).split(',')[1])
    }
    img.onerror = reject
    img.src = src.startsWith('data:') ? src : `data:image/jpeg;base64,${src}`
  })
}

function currentMois() {
  const now = new Date()
  return `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}`
}
function moisLabel(m: string) {
  const [y, mo] = m.split('_')
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
}

export default function MobileBvpPhotos() {
  const navigate = useNavigate()
  const emp = getEmployeeData()
  const isManager = emp?.role === 'manager' || emp?.isManager

  const [photos, setPhotos] = useState<Photo[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [mois, setMois] = useState(currentMois())
  const [previewPhoto, setPreviewPhoto] = useState<{ id: string; imageData: string; fileName: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i)
    return `${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, '0')}`
  })

  const load = async () => {
    setLoading(true)
    try {
      const r = await employeeApi.get(`/employee/me/bvp/photos?mois=${mois}`)
      setPhotos(r.data || [])
    } catch {} finally { setLoading(false) }
  }

  useEffect(() => { load() }, [mois])

  const takePhoto = async () => {
    if (Capacitor.isNativePlatform()) {
      // Sur APK : utiliser l'appareil photo Capacitor Camera
      try {
        const { Camera: CapCamera, CameraResultType, CameraSource } = await import('@capacitor/camera')
        const photo = await CapCamera.getPhoto({
          quality: 70,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Camera,
        })
        if (photo.base64String) {
          const processed = await processImage(photo.base64String)
          await uploadPhoto(processed)
        }
      } catch (e: any) {
        if (!String(e).includes('cancelled')) alert('Impossible d\'accéder à la caméra')
      }
    } else {
      fileInputRef.current?.click()
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async (ev) => {
      try {
        const dataUrl = ev.target?.result as string
        const processed = await processImage(dataUrl)
        await uploadPhoto(processed)
      } catch { alert('Erreur lors du traitement de la photo') }
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const uploadPhoto = async (base64: string) => {
    if (!emp) return
    setUploading(true)
    try {
      const employeeName = `${emp.firstName} ${emp.lastName !== '-' ? emp.lastName : ''}`.trim()
      await employeeApi.post('/employee/me/bvp/photos', {
        employeeId: emp.id,
        employeeName,
        imageData: base64,
      })
      await load()
    } catch { alert('Erreur lors de l\'envoi de la photo') }
    finally { setUploading(false) }
  }

  const deletePhoto = async (id: string) => {
    if (!confirm('Supprimer cette photo ?')) return
    await employeeApi.delete(`/employee/me/bvp/photos/${id}`)
    setPreviewPhoto(null)
    await load()
  }

  const openPhoto = async (id: string) => {
    try {
      const r = await employeeApi.get(`/employee/me/bvp/photos/${id}`)
      setPreviewPhoto({ id, imageData: r.data.imageData, fileName: r.data.fileName })
    } catch { alert('Impossible d\'ouvrir la photo') }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-4 flex items-center gap-3 sticky top-0 z-10"
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: '16px' }}>
        <button onClick={() => navigate('/app/bvp')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-bold text-gray-900 flex items-center gap-2">
            <Image size={18} className="text-blue-600" /> Étiquettes cartons
          </h1>
          <p className="text-xs text-gray-500">{photos.length} photo(s) ce mois</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100">
          <RefreshCw size={16} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex-1 px-4 py-4 flex flex-col gap-4">

        {/* Sélecteur de mois (manager uniquement) */}
        {isManager && (
          <select value={mois} onChange={e => setMois(e.target.value)}
            className="w-full border-2 rounded-xl px-4 py-3 text-sm font-medium bg-white">
            {months.map(m => <option key={m} value={m}>{moisLabel(m)}</option>)}
          </select>
        )}

        {/* Bouton nouvelle photo */}
        <button onClick={takePhoto} disabled={uploading}
          className="w-full bg-blue-600 text-white font-bold py-5 rounded-2xl text-xl flex items-center justify-center gap-3 disabled:opacity-60">
          <Camera size={28} />
          {uploading ? 'Envoi...' : 'Nouvelle photo'}
        </button>

        {/* Input fichier pour web */}
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
          onChange={handleFileChange} className="hidden" />

        {/* Liste des photos */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw size={32} className="mx-auto animate-spin text-blue-500 mb-2" />
            <p className="text-sm text-gray-400">Chargement...</p>
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Image size={48} className="mx-auto mb-3 text-gray-200" />
            <p>Aucune photo ce mois</p>
          </div>
        ) : (
          <div className="space-y-2">
            {photos.map(p => (
              <button key={p.id} onClick={() => openPhoto(p.id)}
                className="w-full bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-4 text-left hover:border-blue-300 transition-colors">
                <div className="w-12 h-12 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Image size={20} className="text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900">{p.date} à {p.heure}</p>
                  <p className="text-sm text-gray-500">{p.employeeName}</p>
                  <p className="text-xs text-gray-400 truncate">{p.fileName}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Modal prévisualisation */}
      {previewPhoto && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 bg-black/80">
            <button onClick={() => setPreviewPhoto(null)} className="text-white p-2">
              <ChevronLeft size={24} />
            </button>
            <p className="text-white text-sm font-medium truncate flex-1 text-center">{previewPhoto.fileName}</p>
            {isManager && (
              <button onClick={() => deletePhoto(previewPhoto.id)} className="text-red-400 p-2">
                <Trash2 size={20} />
              </button>
            )}
          </div>
          <div className="flex-1 flex items-center justify-center p-2">
            <img src={`data:image/jpeg;base64,${previewPhoto.imageData}`}
              alt="Étiquette"
              className="max-w-full max-h-full object-contain rounded-lg" />
          </div>
        </div>
      )}
    </div>
  )
}
